import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { getIntegrations } from '../../_services/ApiService';
import { IntegrationsToolbar, IntegrationsTable } from './components';
import { IntegrationsContext } from './components/IntegrationsContext';
import { useSearchParam } from 'helpers/useSearchParam';

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(3)
}));

const Content = styled('div')(({ theme }) => ({
  padding: theme.spacing(2)
}));

function getFilterOptions(integrations = []) {
  const rawCompanies = [];
  const rawEventTypes = [];

  for (const integration of integrations) {
    rawCompanies.push(integration.companyName);
    rawEventTypes.push(integration.eventType);
  }

  return {
    companies: [...new Set(rawCompanies.sort())],
    eventTypes: [...new Set(rawEventTypes.filter(Boolean).sort())]
  };
}

const IntegrationsList = () => {
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);

  const [companyCriteria, setCompanyCriteria] = useSearchParam('company');
  const [eventTypeCriteria, setEventTypeCriteria] = useSearchParam('eventType');
  const [integrationIdFilter, setIntegrationIdFilter] =
    useSearchParam('integrationId');

  useEffect(() => {
    (async () => {
      try {
        const result = await getIntegrations({ page: page + 1, pageSize });
        setIntegrations(result.data);
        setPageCount(result.total);
      } catch (err) {
        throw err;
      }
    })();
  }, [page, pageSize]);

  const checkCompanyFilter = (integration) => {
    if (!companyCriteria || companyCriteria === 'All') return true;
    if (companyCriteria === 'no-company') return !integration.companyName;
    return integration.companyName === companyCriteria;
  };

  const matchesFilters = (integration) =>
    checkCompanyFilter(integration) &&
    (!eventTypeCriteria ||
      eventTypeCriteria === 'All' ||
      integration.eventType === eventTypeCriteria) &&
    (!integrationIdFilter ||
      (integration._id || '')
        .toLowerCase()
        .includes(integrationIdFilter.toLowerCase()));

  // Keep selected rows visible even if they no longer match filters
  const visibleIntegrations = integrations.filter(
    (integration) =>
      matchesFilters(integration) ||
      selectedIntegrations.indexOf(integration._id) !== -1
  );

  const { companies, eventTypes } = getFilterOptions(integrations);

  const clearAllFilters = () => {
    setCompanyCriteria('');
    setEventTypeCriteria('');
    setIntegrationIdFilter('');
  };

  const contextValue = {
    companies,
    eventTypes,
    onCompanyChange: setCompanyCriteria,
    onEventTypeChange: setEventTypeCriteria,
    onIntegrationIdChange: setIntegrationIdFilter,
    onClearAll: clearAllFilters
  };

  return (
    <IntegrationsContext.Provider value={contextValue}>
      <Root>
        <IntegrationsToolbar
          integrations={integrations}
          selectedIntegrations={selectedIntegrations}
          setIntegrations={setIntegrations}
          setSelectedIntegrations={setSelectedIntegrations}
        />
        <Content>
          <IntegrationsTable
            integrations={visibleIntegrations || []}
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            selectedIntegrations={selectedIntegrations}
            setIntegrations={setIntegrations}
            setPage={setPage}
            setPageSize={setPageSize}
            setSelectedIntegrations={setSelectedIntegrations}
          />
        </Content>
      </Root>
    </IntegrationsContext.Provider>
  );
};

export default IntegrationsList;
